import { makeAutoObservable, runInAction } from 'mobx';
import { z } from 'zod';
import { authApi } from '@/api/auth';
import { branchesApi } from '@/api/branches';
import {
  setAuthContextProvider,
  setOrganizationAccessIssueHandler,
  setSubscriptionRequiredHandler,
  setTerminalInvalidHandler,
  setUnauthorizedHandler,
  type ApiAuthContext,
  type OrganizationAccessIssue,
} from '@/api/client';
import { requiresBranch } from '@/lib/business-profile';
import { setServerCapabilities } from '@/lib/capabilities';
import { normalizeApiError, type ApiError } from '@/lib/errors';
import { loadJson, removeKey, saveJson } from '@/lib/storage';
import {
  organizationRoleSchema,
  salesControlsSchema,
  organizationTypeSchema,
  type AuthUser,
  type Organization,
  type OrganizationRole,
} from '@/types/auth';
import { isPlatformAdminEmail } from '@/lib/platform-admin';
import type { Branch } from '@/types/branches';
import type { Terminal, TerminalPermissions } from '@/types/terminals';

/**
 * AuthStore (V1-02) — token/user/scope selection + persistence.
 *
 * Constraints from the verified backend:
 * - login is the ONLY auth route; there is NO me/logout/refresh
 *   (auth/routes/api.php:3-7) → logout is a pure local purge, restore() may
 *   only trust localStorage, and a POS 401 means the token is dead.
 * - organization/branch/terminal selection is purely client-side, expressed
 *   as X-Organization/X-Branch/X-Terminal-Id headers on POS requests.
 *
 * Wiring (lead integration):
 * - `new AuthStore()` attaches itself to the shared axios clients (auth
 *   headers provider + 401 purge + terminal-invalid recovery). Tests pass
 *   {attachToApi:false} or inject redirect fns.
 * - Other stores register scope listeners (registerScopeListener) so an
 *   organization/branch/terminal change or logout resets scoped state.
 *
 * V2-1 additions:
 * - `hydrated` — nothing protected may be requested before the persisted
 *   session has been read (RootStore's constructor does it).
 * - scope is now three ANSWERABLE levels (hasOrganization/hasBranch/
 *   hasTerminal) rather than one all-or-nothing `isSetupComplete`, because the
 *   office needs an organization, the restaurant floor needs a branch, and only
 *   selling needs a terminal.
 * - a per-device workstation memory keyed by organization, so returning to an
 *   organization returns to the till this device was already set up as.
 */

const STORAGE_KEY = 'sg.auth.v1';

/**
 * Per-DEVICE workstation memory, keyed by organization (V2-1).
 *
 * `sg.auth.v1` holds the ONE scope the session is currently using. This second
 * key remembers, for each organization this device has been set up against,
 * which branch it was standing in and which terminal it was — so switching
 * organization and coming back does not mean re-picking a workstation that
 * never physically moved.
 *
 * The remembered terminal always belongs to the remembered branch; a terminal
 * is never carried across a branch, which is the same rule restore() applies.
 */
const WORKSTATION_KEY = 'sg.workstation.v1';

/** Broadest scope that changed. org ⊃ branch ⊃ terminal; logout clears everything. */
export type AuthScopeChange = 'organization' | 'branch' | 'terminal' | 'logout';
export type AuthScopeListener = (change: AuthScopeChange) => void;

// Persisted snapshot is untrusted input — validate on restore, drop if stale/garbled.
const persistedOrganizationSchema = z.object({
  salesControls: salesControlsSchema.optional(),
  id: z.number(),
  name: z.string(),
  domain: z.string(),
  type: organizationTypeSchema.nullable(),
  // V1-ROLES: additive. `.catch(null)` so a snapshot written before roles
  // existed still restores instead of being discarded as garbled.
  role: organizationRoleSchema.nullable().catch(null).default(null),
  status: z.enum(['active', 'suspended']).nullable().catch(null).default(null),
});
const persistedAuthSchema = z.object({
  token: z.string().min(1),
  user: z.object({
    id: z.number(),
    first_name: z.string(),
    last_name: z.string(),
    email: z.string(),
  }),
  organizations: z.array(persistedOrganizationSchema).default([]),
  organization: persistedOrganizationSchema.nullable(),
  branch: z
    .object({
      id: z.number(),
      name: z.string(),
    })
    .loose()
    .nullable(),
  terminal: z
    .object({
      id: z.number(),
      identifier: z.string(),
      name: z.string(),
    })
    .loose()
    .nullable(),
});

const persistedBranchSchema = z.object({ id: z.number(), name: z.string() }).loose();
const persistedTerminalSchema = z
  .object({ id: z.number(), identifier: z.string(), name: z.string() })
  .loose();

/**
 * Two levels, because a workstation is two different facts.
 *
 * A BRANCH is where the device physically is: one per organization, and the
 * same for whoever is standing at it.
 *
 * A TERMINAL is a till inside a branch, and it is ASSIGNED — /terminals/my is
 * scoped to the caller — so it is remembered per organization AND branch AND
 * user. Keying terminals by organization alone lost the till every time
 * someone looked at another branch and came back.
 */
const persistedWorkstationSchema = z.object({
  /** organizationId → the branch this device was last standing in. */
  branches: z.record(z.string(), persistedBranchSchema).catch({}).default({}),
  /** `organizationId:branchId` → the till a user nominated there. */
  terminals: z
    .record(
      z.string(),
      z.object({
        userId: z.number().nullable().catch(null).default(null),
        terminal: persistedTerminalSchema,
      }),
    )
    .catch({})
    .default({}),
});
/**
 * The validated map in DOMAIN terms.
 *
 * The zod schema above is the boundary check; this is what the store works
 * with afterwards, exactly as restore() re-types the session snapshot. Domain
 * interfaces carry no index signature, so they cannot be assigned into zod's
 * inferred loose-object types without going through here.
 */
interface StoredWorkstations {
  branches: Record<string, Branch>;
  terminals: Record<string, { userId: number | null; terminal: Terminal }>;
}

interface RememberedWorkstation {
  branch: Branch | null;
  terminal: Terminal | null;
}

const EMPTY_WORKSTATION: RememberedWorkstation = { branch: null, terminal: null };

const tillKey = (organizationId: number, branchId: number | null) =>
  `${organizationId}:${branchId ?? 'none'}`;

/**
 * A terminal only ever belongs to one branch. Anything else is a stale
 * snapshot and is dropped rather than sent as an X-Terminal-Id the server will
 * reject — the same rule restore() applies to `sg.auth.v1`.
 */
/**
 * /setup carrying where to come back to and the scope the API rejected.
 *
 * Built inline rather than imported from features/auth/routing.ts: a store
 * reaching into a feature module would invert the dependency direction the
 * whole app is arranged around. `safeNextPath` discards a `/setup` value on the
 * way back in, so re-entering setup from setup cannot loop.
 */
function setupUrl(): string {
  const here = window.location.pathname + window.location.search;
  return `/setup?next=${encodeURIComponent(here)}&required=terminal`;
}

function terminalBelongsTo(terminal: Terminal | null, branch: Branch | null): boolean {
  if (!terminal) return false;
  const branchId = (terminal as { branch_id?: unknown }).branch_id;
  if (!branch) return branchId == null;
  return typeof branchId === 'number' ? branchId === branch.id : true;
}

export interface AuthStoreOptions {
  /** Attach to the shared axios clients on construction (default true). */
  attachToApi?: boolean;
  /** After a 401 purge; default hard-redirects to /login. */
  onUnauthorizedRedirect?: () => void;
  /** After terminal invalidation (403/404); default hard-redirects to /setup. */
  onTerminalInvalidRedirect?: () => void;
  /** After subscription expiry; default hard-redirects to the merchant billing portal. */
  onSubscriptionRequiredRedirect?: () => void;
}

export class AuthStore {
  token: string | null = null;
  user: AuthUser | null = null;
  organizations: Organization[] = [];
  organization: Organization | null = null;
  branch: Branch | null = null;
  private receiptBranchState: {
    organizationId: number;
    token: string;
    count: number;
    onlyBranchId: number | null;
  } | null = null;
  private receiptBranchEpoch = 0;
  terminal: Terminal | null = null;
  organizationAccessIssue: OrganizationAccessIssue = null;
  isLoading = false;
  /** Last login error, normalized. Field-level 422s live in error.fieldErrors. */
  error: ApiError | null = null;
  /**
   * False until the persisted session has been read (RootStore's constructor
   * calls restore()) or a login has landed.
   *
   * Nothing may fire a PROTECTED request while this is false: a request sent
   * before the token is in the store goes out unsigned, comes back
   * 401 "Unauthenticated.", and — because the store holding the 401 handler had
   * no token to purge — leaves the screen saying "Signed in" next to a
   * retryable auth error that can never succeed. Guards and setup wait on it.
   */
  hydrated = false;
  /**
   * True once a reconcile against `GET {pos}/me` has completed for the
   * organization currently in scope. Capability-sensitive inventory UI waits
   * for this attempt so stale local roles cannot briefly expose stock work.
   */
  reconciled = false;

  private options: AuthStoreOptions;
  private reconcileAbort: AbortController | null = null;
  private reconcileRequest: {
    organizationId: number;
    token: string;
    promise: Promise<void>;
  } | null = null;
  private scopeListeners = new Set<AuthScopeListener>();

  /** Receipt-only display rule; branch IDs and request scope stay intact. */
  get receiptBranchName(): string | null {
    const state = this.receiptBranchState;
    return state?.organizationId === this.organization?.id &&
      state?.token === this.token &&
      state?.count === 1 &&
      state.onlyBranchId === this.branch?.id
      ? null
      : (this.branch?.name ?? null);
  }

  /** Receipt identity is needed when more than one active location trades. */
  async refreshReceiptBranchCount(): Promise<void> {
    const organizationId = this.organization?.id;
    const token = this.token;
    if (organizationId == null || !token) return;
    const epoch = ++this.receiptBranchEpoch;
    this.receiptBranchState = null;
    try {
      const branches = (await branchesApi.list()).filter((branch) => branch.is_active);
      if (
        epoch !== this.receiptBranchEpoch ||
        this.organization?.id !== organizationId ||
        this.token !== token
      )
        return;
      runInAction(() => {
        this.receiptBranchState = {
          organizationId,
          token,
          count: new Set(branches.map((branch) => branch.id)).size,
          onlyBranchId: branches.length === 1 ? branches[0]!.id : null,
        };
      });
    } catch {
      // Unknown count preserves the existing branch identity; a metadata failure
      // must neither block a sale nor conceal a potentially multi-branch location.
    }
  }

  constructor(options: AuthStoreOptions = {}) {
    this.options = options;
    makeAutoObservable<
      AuthStore,
      'options' | 'scopeListeners' | 'reconcileAbort' | 'reconcileRequest'
    >(this, {
      options: false,
      scopeListeners: false,
      reconcileAbort: false,
      reconcileRequest: false,
    });
    if (options.attachToApi !== false) this.attachToApi();
    // Hydration is a construction invariant: there is no moment at which a
    // caller can hold an AuthStore that has not yet answered "is there a
    // session?". Reading it later — from a React render, as the app used to —
    // is what let a protected request go out unsigned and come back
    // 401 "Unauthenticated." underneath a screen headed "Signed in".
    // Synchronous and idempotent, so calling restore() again is harmless.
    this.restore();
  }

  // ── computeds ────────────────────────────────────────────────────────────

  get isAuthenticated(): boolean {
    return !!this.token;
  }

  /** UX only; every platform API repeats this check on the server. */
  get isPlatformAdmin(): boolean {
    return isPlatformAdminEmail(this.user?.email);
  }

  /** Organization chosen — the least a tenant-scoped request can run with. */
  get hasOrganization(): boolean {
    return !!this.organization;
  }

  /** Branch scope covered — selected, or not required for this organization. */
  get hasBranch(): boolean {
    return !!(
      this.organization &&
      (this.branch || !requiresBranch(this.organization.type ?? null))
    );
  }

  /** Terminal chosen — required to sell, tender or own a day session. */
  get hasTerminal(): boolean {
    return !!(this.hasBranch && this.terminal);
  }

  get isSetupComplete(): boolean {
    return this.hasTerminal;
  }

  /**
   * Membership role for the SELECTED organization, or null when the backend
   * did not report one. UX only — pos-api authorizes every request
   * (see src/lib/capabilities.ts).
   */
  get role(): OrganizationRole | null {
    return this.organization?.role ?? null;
  }

  get displayName(): string {
    if (!this.user) return '';
    return [this.user.first_name, this.user.last_name].filter(Boolean).join(' ');
  }

  // ── API wiring ───────────────────────────────────────────────────────────

  /** Header context for the POS client — pure client-side scope selection. */
  getApiContext(): ApiAuthContext {
    return {
      token: this.token,
      organizationId: this.organization?.id ?? null,
      branchId: requiresBranch(this.organization?.type) ? (this.branch?.id ?? null) : null,
      terminalId: this.terminal?.id ?? null,
      terminalIdentifier: this.terminal?.identifier ?? null,
    };
  }

  /** Bind this store to the shared axios clients. Last store attached wins. */
  attachToApi(): void {
    setAuthContextProvider(() => this.getApiContext());
    setUnauthorizedHandler(() => this.handleUnauthorized());
    setTerminalInvalidHandler(() => this.handleTerminalInvalid());
    setSubscriptionRequiredHandler(() => this.handleSubscriptionRequired());
    setOrganizationAccessIssueHandler((issue) => this.handleOrganizationAccessIssue(issue));
  }

  private handleUnauthorized(): void {
    if (!this.token) return; // already logged out — don't loop
    this.purge();
    this.notifyScope('logout');
    (this.options.onUnauthorizedRedirect ?? (() => window.location.assign('/login')))();
  }

  private handleTerminalInvalid(): void {
    // The redirect fires even when no terminal is selected: a route that
    // requires one can now be entered without one (the office and the
    // restaurant floor deliberately do not), and "Invalid Terminal" from there
    // means the screen needs a workstation, not that a stale id needs clearing.
    if (this.terminal) this.clearTerminal();
    (this.options.onTerminalInvalidRedirect ?? (() => window.location.assign(setupUrl())))();
  }

  private handleSubscriptionRequired(): void {
    if (window.location.pathname === '/billing') return;
    (this.options.onSubscriptionRequiredRedirect ?? (() => window.location.assign('/billing')))();
  }

  private handleOrganizationAccessIssue(issue: OrganizationAccessIssue): void {
    this.organizationAccessIssue = issue;
  }

  // ── scope listeners (reset seam for other stores) ────────────────────────

  /**
   * Register a callback fired AFTER an organization/branch/terminal change or
   * logout. The lead wires each scoped store's reset here (RootStore
   * registration step). Returns a disposer.
   */
  registerScopeListener(listener: AuthScopeListener): () => void {
    this.scopeListeners.add(listener);
    return () => this.scopeListeners.delete(listener);
  }

  private notifyScope(change: AuthScopeChange): void {
    for (const listener of this.scopeListeners) {
      try {
        listener(change);
      } catch {
        // one listener failing must not stop the others
      }
    }
  }

  // ── auth flows ───────────────────────────────────────────────────────────

  async login(email: string, password: string): Promise<boolean> {
    this.isLoading = true;
    this.error = null;
    try {
      const result = await authApi.login(email, password);
      runInAction(() => {
        this.organizationAccessIssue = null;
        this.token = result.token;
        this.user = result.user;
        this.organizations = result.organizations;
        this.hydrated = true;
        // Auto-select only when the user belongs to exactly one organization;
        // more than one is a real choice and /setup asks for it.
        const only = result.organizations.length === 1 ? (result.organizations[0] ?? null) : null;
        this.organization = only;
        this.organizationAccessIssue = only?.status === 'suspended' ? 'suspended' : null;
        // A workstation this device already configured for that organization is
        // restored, so a cashier signing back in at their own counter does not
        // re-pick the till they never left.
        const remembered = only ? this.recallWorkstation(only.id) : EMPTY_WORKSTATION;
        this.branch = remembered.branch;
        this.terminal = remembered.terminal;
        this.persist();
      });
      // The login payload is `auth`'s view: it carries `type` only via the
      // eager-loaded relation and knows nothing about capabilities. Ask the
      // tenant-aware side for the authoritative version. Not awaited — a fresh
      // sign-in must land on the app immediately, and this only ever refines
      // what is already there.
      void this.reconcile();
      return true;
    } catch (e) {
      const err = normalizeApiError(e);
      runInAction(() => {
        // Login failure is HTTP 400 {"error":"Invalid credentials"} — surface
        // the product wording, not the raw envelope. 422 keeps fieldErrors.
        this.error = err.status === 400 ? { ...err, message: 'Wrong email or password' } : err;
      });
      return false;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  /**
   * Restore the persisted snapshot. Invalid/stale payloads are discarded.
   *
   * Synchronous and idempotent, and it ALWAYS ends hydrated — "there was no
   * session" is a hydrated answer too, and a guard that waited for a token
   * would hang forever on a fresh device.
   */
  restore(): void {
    const raw = loadJson<unknown>(STORAGE_KEY);
    if (raw === null) {
      this.hydrated = true;
      return;
    }
    const parsed = persistedAuthSchema.safeParse(raw);
    if (!parsed.success) {
      removeKey(STORAGE_KEY);
      this.hydrated = true;
      return;
    }
    const saved = parsed.data;
    this.token = saved.token;
    this.user = saved.user;
    this.organizations = saved.organizations;
    this.organization = saved.organization;
    this.organizationAccessIssue = saved.organization?.status === 'suspended' ? 'suspended' : null;
    this.branch = (saved.branch as Branch | null) ?? null;
    let terminal = (saved.terminal as Terminal | null) ?? null;
    if (terminal && typeof terminal.warehouse_id !== 'number') {
      terminal = { ...terminal, warehouse_id: null };
    }
    // Owner decision 2026-08-03: a terminal that does not belong to the
    // selected branch is never allowed — drop it on restore (the user
    // re-picks on /setup). Terminals without a branch_id are left alone.
    if (
      terminal &&
      this.branch &&
      typeof (terminal as { branch_id?: unknown }).branch_id === 'number' &&
      (terminal as { branch_id: number }).branch_id !== this.branch.id
    ) {
      terminal = null;
    }
    this.terminal = terminal;
    // Seed the per-organization memory from the session snapshot. A device that
    // was already set up before that memory existed would otherwise forget its
    // own till the first time someone switched organization and back.
    this.backfillWorkstation();
    this.persist();
    this.hydrated = true;
  }

  /**
   * Re-ask the server who this session is, and correct the snapshot in place.
   *
   * ── THE PROBLEM ────────────────────────────────────────────────────────
   * `auth` has one route and no `/me`, so the user, the organization's `type`
   * and the membership `role` were learned once at login and written to
   * localStorage, where they stayed until someone signed out and back in. That
   * made signing out the only way to pick up a changed org type, a promotion, a
   * demotion, a new membership or a revoked one — which is exactly the
   * "logout/login karna par raha hai" the owner hit. pos-api now answers the
   * same question on demand.
   *
   * ── THE THREE RULES THIS OBEYS ────────────────────────────────────────
   * 1. DO NOT BLOCK BOOT. It never touches `hydrated`. `hydrated` means "the
   *    persisted session has been read", and that is answered synchronously in
   *    the constructor. Gating it on a network call would resurrect the V2-1
   *    defect where a guard answered "not signed in" before it had looked, and
   *    would put a spinner in front of an offline counter for no reason.
   * 2. DO NOT LOCK ANYONE OUT ON FAILURE. A dead API, a sleeping laptop or a
   *    lost network leaves the persisted session exactly as it was. The single
   *    exception is a 401, and that is not handled here at all — posClient's
   *    interceptor already purges on it, because a rejected token is a dead
   *    session, not a stale field.
   * 3. DO NOT POLL. Reconcile at boot, on organization switch and when the
   *    workstation returns to a visible tab or regains connectivity. Platform
   *    changes can happen during a shift; the server enforces them on each action.
   *
   * Safe to call when signed out or before an organization is chosen: it simply
   * does nothing, since the request would have no tenant to be scoped to.
   */
  reconcile(): Promise<void> {
    if (!this.token || !this.organization) return Promise.resolve();

    const askedFor = this.organization.id;
    if (
      this.reconcileRequest?.organizationId === askedFor &&
      this.reconcileRequest.token === this.token
    ) {
      return this.reconcileRequest.promise;
    }

    // An organization switch during a slow reconcile would otherwise land the
    // OLD tenant's role and capabilities on the NEW one.
    this.reconcileAbort?.abort();
    const controller = new AbortController();
    this.reconcileAbort = controller;
    const promise = this.reconcileAccess(askedFor, controller);
    this.reconcileRequest = { organizationId: askedFor, token: this.token, promise };
    return promise;
  }

  private async reconcileAccess(askedFor: number, controller: AbortController): Promise<void> {
    try {
      const me = await authApi.me({ signal: controller.signal });
      runInAction(() => {
        // Scope moved while the answer was in flight — it describes a tenant
        // nobody is looking at any more. Dropping it is correct; the switch
        // started its own reconcile.
        if (controller.signal.aborted || this.organization?.id !== askedFor) return;

        this.user = me.user;

        // The server's list REPLACES the login snapshot's: an organization
        // added since login is missing from the old one, and one whose
        // membership was revoked is still in it, offering a tenant every
        // request will now refuse.
        if (me.organizations.length > 0) this.organizations = me.organizations;

        // The selected organization, as the server describes it TODAY — this is
        // the line that fixes a changed `type`, since organizations.type is the
        // single switch the whole console reads (lib/business-profile.ts).
        const fresh =
          me.organization?.id === askedFor
            ? me.organization
            : (me.organizations.find((o) => o.id === askedFor) ?? null);
        if (fresh) this.organization = fresh;

        setServerCapabilities(me.capabilities, me.capabilityVocabulary);
        this.reconciled = true;
        this.persist();
      });
    } catch {
      // Rule 2. The persisted session stands, the local capability table stays
      // in charge, and the next boot or organization switch asks again. There
      // is deliberately no retry loop and no error surfaced: a correction that
      // did not arrive is not something the person at the counter can act on.
    } finally {
      if (this.reconcileAbort === controller) {
        runInAction(() => {
          if (!controller.signal.aborted && this.organization?.id === askedFor) {
            this.reconciled = true;
          }
          this.reconcileAbort = null;
          this.reconcileRequest = null;
        });
      }
    }
  }

  /**
   * Sign out: revoke the token server-side, then purge this device.
   *
   * The purge is LOCAL AND UNCONDITIONAL, and it happens first. Signing out is
   * the one action a person must never be unable to complete — a counter with
   * no network, an API that is down, or a token the server has already
   * forgotten must all still clear the screen. Waiting on the network here
   * would mean the worst case is the previous user's session staying visible.
   *
   * The revoke is fire-and-forget for the same reason, and it needs the token
   * captured BEFORE the purge, since purge() is what removes it. Until
   * `POST {pos}/auth/logout` existed this was a local purge and nothing
   * else, which left the token live for its full year: signing out of a shared
   * till removed the session from view without ending it.
   */
  logout(): void {
    const token = this.token;
    this.purge();
    this.notifyScope('logout');
    if (token) {
      void authApi.logout(token).catch(() => {
        // Already gone from this device. A token that outlives its sign-out is
        // worth less than a sign-out that refuses to complete, and there is
        // nothing the person at the counter could do with the error.
      });
    }
  }

  /** Clear all auth state + storage without side effects (also used by the 401 path). */
  private purge(): void {
    this.token = null;
    this.user = null;
    this.organizations = [];
    this.organization = null;
    this.branch = null;
    this.terminal = null;
    this.error = null;
    this.reconciled = false;
    this.organizationAccessIssue = null;
    this.reconcileAbort?.abort();
    this.reconcileAbort = null;
    this.reconcileRequest = null;
    // Grants belong to a membership, not to the device. Leaving one signed-in
    // user's permissions behind for the next is not a stale field, it is the
    // wrong person's authority.
    setServerCapabilities(null, null);
    removeKey(STORAGE_KEY);
  }

  // ── scope selection (pure client-side; becomes headers) ─────────────────

  selectOrganization(org: Organization): void {
    this.organizationAccessIssue = org.status === 'suspended' ? 'suspended' : null;
    // Re-selecting the organization already in scope would reset every scoped
    // store to land back where it started.
    if (this.organization?.id === org.id) {
      this.organization = org;
      this.persist();
      return;
    }
    this.organization = org;
    // A branch and a terminal belong to ONE organization: the previous
    // selections are never valid here and are dropped before anything can send
    // them as headers. What replaces them is this device's own remembered
    // workstation for the organization being entered — nothing carried over.
    const remembered = this.recallWorkstation(org.id);
    this.branch = remembered.branch;
    this.terminal = remembered.terminal;
    // Capabilities are per-MEMBERSHIP: the role here may not be the role there.
    // Cleared before anything renders, so the moment between switching and the
    // reconcile landing falls back to the local table rather than showing the
    // previous tenant's grants.
    this.reconciled = false;
    setServerCapabilities(null, null);
    this.persist();
    this.notifyScope('organization');
    // The `type` of the organization being entered decides the entire mode
    // (restaurant vs retail), and the snapshot's copy of it may be months old.
    void this.reconcile();
  }

  adoptOrganization(org: Organization): void {
    this.organizations = [
      ...this.organizations.filter((available) => available.id !== org.id),
      org,
    ];
    this.selectOrganization(org);
  }

  selectBranch(branch: Branch): void {
    if (!requiresBranch(this.organization?.type)) return;
    if (this.branch?.id === branch.id) {
      this.branch = branch;
      this.persist();
      return;
    }
    this.branch = branch;
    // Only the till this user already nominated IN THIS BRANCH survives; the
    // one from the branch being left never does, and is not forgotten either —
    // coming back to that branch finds it again.
    const organizationId = this.organization?.id ?? null;
    this.terminal = organizationId === null ? null : this.recallTerminal(organizationId, branch);
    this.rememberWorkstation();
    this.persist();
    this.notifyScope('branch');
  }

  selectTerminal(terminal: Terminal): void {
    this.terminal = terminal;
    this.rememberWorkstation();
    this.persist();
    this.notifyScope('terminal');
  }

  /** Same register, refreshed configuration: preserve the cart and its operation key. */
  updateTerminalPermissions(terminalId: number, permissions: TerminalPermissions): void {
    if (this.terminal?.id !== terminalId) return;
    this.terminal = { ...this.terminal, permissions };
    this.rememberWorkstation();
    this.persist();
  }

  clearOrganization(): void {
    this.organizationAccessIssue = null;
    this.organization = null;
    this.branch = null;
    this.terminal = null;
    this.persist();
    this.notifyScope('organization');
  }

  clearBranch(): void {
    this.branch = null;
    this.terminal = null;
    this.rememberWorkstation();
    this.persist();
    this.notifyScope('branch');
  }

  clearTerminal(): void {
    this.terminal = null;
    this.rememberWorkstation();
    this.persist();
    this.notifyScope('terminal');
  }

  // ── per-device workstation memory ────────────────────────────────────────

  /**
   * Record the current branch/terminal against the current organization.
   *
   * Deliberately NOT cleared by logout: this is the identity of a physical
   * till, not of the person standing at it, and a shared counter device that
   * forgot which till it was on every sign-out would send every cashier back
   * through /setup at the start of every shift.
   */
  private rememberWorkstation(): void {
    const organizationId = this.organization?.id;
    if (organizationId == null) return;
    const all = this.readWorkstations();

    if (this.branch) all.branches[String(organizationId)] = this.branch;
    else delete all.branches[String(organizationId)];

    const key = tillKey(organizationId, this.branch?.id ?? null);
    // Restaurant memory stays branch-scoped; retail memory is per organization.
    // Record absence by deleting the entry instead of storing null.
    if (
      this.terminal &&
      (!requiresBranch(this.organization?.type) || terminalBelongsTo(this.terminal, this.branch))
    ) {
      all.terminals[key] = { userId: this.user?.id ?? null, terminal: this.terminal };
    } else {
      delete all.terminals[key];
    }
    saveJson(WORKSTATION_KEY, all);
  }

  /**
   * Seed a device that was set up before this memory existed. Never overwrites:
   * every path that changes the workstation already keeps the entry current, so
   * restore() must only fill a gap, not stamp whatever the session snapshot
   * happens to hold over a newer answer.
   */
  private backfillWorkstation(): void {
    const organizationId = this.organization?.id;
    if (organizationId == null || !this.branch) return;
    const all = this.readWorkstations();
    if (all.branches[String(organizationId)]) return;
    this.rememberWorkstation();
  }

  private recallWorkstation(organizationId: number | null): RememberedWorkstation {
    if (organizationId == null) return EMPTY_WORKSTATION;
    const rememberedBranch = this.readWorkstations().branches[String(organizationId)] ?? null;
    const branch = requiresBranch(this.organization?.type) ? rememberedBranch : null;
    return {
      branch,
      terminal:
        this.recallTerminal(organizationId, branch) ??
        (branch === null && rememberedBranch
          ? this.recallTerminal(organizationId, rememberedBranch)
          : null),
    };
  }

  /**
   * The till this USER nominated in this branch on this device.
   *
   * Terminals are assignments, not places: /terminals/my is scoped to the
   * caller, so handing one user's till to the next person to sign in at the
   * same counter would only buy them a 403 "Access Denied" from
   * PosTerminalMiddleware on their first request. Branches carry over between
   * users; terminals do not.
   */
  private recallTerminal(organizationId: number, branch: Branch | null): Terminal | null {
    const entry = this.readWorkstations().terminals[tillKey(organizationId, branch?.id ?? null)];
    if (!entry) return null;
    if (entry.userId == null || entry.userId !== this.user?.id) return null;
    return !requiresBranch(this.organization?.type) || terminalBelongsTo(entry.terminal, branch)
      ? entry.terminal
      : null;
  }

  private readWorkstations(): StoredWorkstations {
    const raw = loadJson<unknown>(WORKSTATION_KEY);
    const parsed = persistedWorkstationSchema.safeParse(raw ?? {});
    if (!parsed.success) {
      removeKey(WORKSTATION_KEY);
      return { branches: {}, terminals: {} };
    }
    return parsed.data as unknown as StoredWorkstations;
  }

  // ── persistence ──────────────────────────────────────────────────────────

  private persist(): void {
    // Older retail setup silently selected a branch and hid branchless tills.
    // Clear that device context on restore/login/reconcile, preserving its till.
    if (this.organization && !requiresBranch(this.organization.type)) {
      const hadBranch = this.branch !== null;
      this.branch = null;
      this.rememberWorkstation();
      if (hadBranch) this.notifyScope('branch');
    }
    if (!this.token || !this.user) return;
    saveJson(STORAGE_KEY, {
      token: this.token,
      user: this.user,
      organizations: this.organizations,
      organization: this.organization,
      branch: this.branch,
      terminal: this.terminal,
    });
  }
}

export { STORAGE_KEY as AUTH_STORAGE_KEY, WORKSTATION_KEY as WORKSTATION_STORAGE_KEY };
